
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TenderSubscriptions } from "@/components/dashboard/TenderSubscriptions";
import { NotificationFrequency } from "@/types/notification";
import { NotificationSection, NotificationFormFields } from "@/components/tender-agent/sections/NotificationSection";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import PageLayout from "@/components/layout/PageLayout";
import { Bell, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const NotificationSettings = () => {
  const session = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const supabase = useSupabaseClient();
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const { t, i18n } = useTranslation();
  const isDanish = i18n.language === 'da';

  const form = useForm<NotificationFormFields>({
    defaultValues: {
      notification_frequency: "daily" as NotificationFrequency,
      email_notifications_enabled: true,
    },
  });

  useEffect(() => {
    if (!session) {
      navigate("/login");
      return;
    }

    const loadPreferences = async () => {
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('notification_frequency, email_notifications_enabled')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        if (profileData) {
          form.reset({
            notification_frequency: (profileData.notification_frequency || 'daily') as NotificationFrequency,
            email_notifications_enabled: profileData.email_notifications_enabled ?? true,
          });
        }
      } catch (error) {
        console.error("Error loading preferences:", error);
        toast({
          title: isDanish ? "Fejl" : "Error",
          description: isDanish ? "Kunne ikke indlæse notifikationsindstillinger" : "Failed to load notification preferences",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (!currentSession) {
        navigate("/login");
      }
    });

    return () => subscription.unsubscribe();
  }, [session, navigate, supabase, toast, form, isDanish]);

  const onSubmit = async (data: NotificationFormFields) => {
    if (!session?.user?.id) {
      navigate("/login");
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          notification_frequency: data.notification_frequency,
          email_notifications_enabled: data.email_notifications_enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (error) throw error;

      toast({
        title: isDanish ? "Succes" : "Success",
        description: isDanish ? "Notifikationsindstillinger gemt" : "Notification preferences saved successfully",
      });
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast({
        title: isDanish ? "Fejl" : "Error",
        description: isDanish ? "Kunne ikke gemme notifikationsindstillinger" : "Failed to save notification preferences",
        variant: "destructive",
      });
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTest(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No user found");
      }

      const { error } = await supabase.functions.invoke('send-test-email', {
        body: { userId: user.id },
      });

      if (error) throw error;

      toast({
        title: isDanish ? "Succes" : "Success",
        description: isDanish ? "Test email sendt. Tjek din indbakke." : "Test email sent successfully. Please check your inbox.",
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: isDanish ? "Fejl" : "Error",
        description: isDanish ? "Kunne ikke sende test email" : "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setSendingTest(false);
    }
  };

  // Added section to trigger manual tender check
  const handleManualTenderCheck = async () => {
    try {
      setSendingTest(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No user found");
      }

      toast({
        title: isDanish ? "Information" : "Information",
        description: isDanish ? "Starter søgning efter nye udbud..." : "Starting check for new tenders...",
      });

      const { error } = await supabase.functions.invoke('check-new-tenders', {
        body: { userId: user.id },
      });

      if (error) throw error;

      toast({
        title: isDanish ? "Succes" : "Success",
        description: isDanish ? "Søgningen er fuldført. Tjek din e-mail for resultater." : "Tender check completed. Please check your email for results.",
      });
    } catch (error) {
      console.error('Error checking for new tenders:', error);
      toast({
        title: isDanish ? "Fejl" : "Error",
        description: isDanish ? "Kunne ikke tjekke for nye udbud" : "Failed to check for new tenders",
        variant: "destructive",
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (!session) {
    navigate("/login");
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageLayout
      title={isDanish ? "Notifikationsindstillinger" : "Notification Settings"}
      description={isDanish ? "Administrer dine notifikationspræferencer og abonnementer" : "Manage your notification preferences and subscriptions"}
      icon={<Bell className="h-6 w-6" />}
    >
      <div className="container mx-auto px-4">
        <div className="grid gap-6 py-8">
          <TenderSubscriptions />
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              {isDanish ? "Email Notifikationer" : "Email Notifications"}
            </h2>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <NotificationSection form={form} />
                <div className="flex flex-col space-y-4">
                  <Button type="submit">
                    {isDanish ? "Gem Indstillinger" : "Save Preferences"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                  >
                    {sendingTest ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isDanish ? 'Sender...' : 'Sending...'}
                      </>
                    ) : (
                      isDanish ? 'Send Test Email' : 'Send Test Email'
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="secondary"
                    onClick={handleManualTenderCheck}
                    disabled={sendingTest}
                  >
                    {sendingTest ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isDanish ? 'Søger...' : 'Checking...'}
                      </>
                    ) : (
                      isDanish ? 'Tjek for nye udbud nu' : 'Check for new tenders now'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default NotificationSettings;
